import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MobileTradeShow from "./MobileTradeShow";
import DesktopTradeShow from "./DesktopTradeShow";
import { COLORS } from "../../constants";
import useSystemHook from "./../../hooks/SystemHook";
import { ApiCallStatus } from "../../utils/constants";

export const DEFAULT_YEAR = "2026";

const TradeShow = ({
  onSubmitClick,
  control,
  errors,
  genericDataLoaded,
  createEventResponseCallStatus,
  setCreateEventResponseCallStatus,
  setIsRsvpModalOpen,
  reset,
}) => {
  const { isMobile, isTablet, isDesktop } = useSystemHook();
  const { year } = useParams();
  const navigate = useNavigate();
  const [imageUrls, setImageUrls] = useState([]);

  const knownImageNames = {
    2025: Array.from(
      { length: 148 },
      (_, i) => `TR2025_${String(i + 1).padStart(3, "0")}`,
    ),
    2024: Array.from(
      { length: 137 },
      (_, i) => `TR2024_${String(i + 1).padStart(3, "0")}`,
    ),
    2023: Array.from(
      { length: 28 }, // Adjust this number based on actual image count from the folder itself!
      (_, i) => `TR2023_${String(i + 1).padStart(3, "0")}`,
    ),
    2022: Array.from(
      { length: 30 },
      (_, i) => `TR2022_${String(i + 1).padStart(3, "0")}`,
    ),
    2019: Array.from(
      { length: 116 },
      (_, i) => `TR2019_${String(i + 1).padStart(3, "0")}`,
    ),
  };

  const availableYears = Object.keys(knownImageNames).sort((a, b) => b - a);
  const validYears = [DEFAULT_YEAR, ...availableYears];

  const [selectedYear, setSelectedYear] = useState(
    validYears.includes(year) ? year : DEFAULT_YEAR,
  );

  // Keeps the URL as the source of truth: redirect to the canonical /tradeshow/2026 when the
  // year segment is missing or unrecognised, mirroring how /merchandising/:category? behaves.
  useEffect(() => {
    if (!year || !validYears.includes(year)) {
      navigate(`/tradeshow/${DEFAULT_YEAR}`, { replace: true });
      return;
    }
    setSelectedYear(year);
  }, [year]);

  const url = "https://argusweb.azureedge.net/argus-web/trade-show";

  useEffect(() => {
    const imageExtension = ["2022", "2023"].includes(selectedYear)
      ? ".jpeg"
      : ".jpg";
    const urls =
      knownImageNames[selectedYear]?.map(
        (name) => `${url}/${selectedYear}/${name}${imageExtension}`,
      ) || [];
    setImageUrls(urls);
  }, [selectedYear]);

  useEffect(() => {
    if (createEventResponseCallStatus === ApiCallStatus.Succeeded) {
      setIsRsvpModalOpen(true);
    }
  }, [createEventResponseCallStatus, setIsRsvpModalOpen]);

  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
    navigate(`/tradeshow/${newYear}`, { replace: true });
  };

  const handleRsvpModalClose = () => {
    setIsRsvpModalOpen(false);
    reset();
    setCreateEventResponseCallStatus(ApiCallStatus.NotStarted);
  };

  return (
    <>
      {isMobile ? (
        <MobileTradeShow
          isRsvpModalOpen={
            createEventResponseCallStatus === ApiCallStatus.Succeeded ||
            createEventResponseCallStatus === ApiCallStatus.Conflict
          }
          setIsRsvpModalOpen={setIsRsvpModalOpen}
          onRsvpModalClose={handleRsvpModalClose}
          selectedYear={selectedYear}
          handleYearChange={handleYearChange}
          control={control}
          errors={errors}
          onSubmitClick={onSubmitClick}
          isMobile={isMobile}
          imageUrls={imageUrls}
          availableYears={availableYears}
          setSelectedYear={setSelectedYear}
          createEventResponseCallStatus={createEventResponseCallStatus}
        />
      ) : (
        <DesktopTradeShow
          isRsvpModalOpen={
            createEventResponseCallStatus === ApiCallStatus.Succeeded ||
            createEventResponseCallStatus === ApiCallStatus.Conflict
          }
          setIsRsvpModalOpen={setIsRsvpModalOpen}
          onRsvpModalClose={handleRsvpModalClose}
          selectedYear={selectedYear}
          handleYearChange={handleYearChange}
          control={control}
          errors={errors}
          onSubmitClick={onSubmitClick}
          isMobile={isMobile}
          imageUrls={imageUrls}
          availableYears={availableYears}
          setSelectedYear={setSelectedYear}
          createEventResponseCallStatus={createEventResponseCallStatus}
        />
      )}
    </>
  );
};

export default TradeShow;
